import Typography from '@/common/components/base/typography/Typography';
import LoadingPage from '@/common/pages/LoadingPage';
import ChartImage from '@/modules/Statistics/assets/icons/chart.svg?react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Box, Flex } from 'styled-system/jsx';
import { PERFORMANCE_MODAL_CONTENT } from '../../data/PerformanceModal.data';
import { GetPerformanceSummaryQueryResponseType } from '../../queries/getPerformanceSummary/getPerformanceSummaryQuery.type';
import { getPerformanceSummaryQueryOptions } from '../../queries/getPerformanceSummary/getPerformanceSummaryQueryOptions';
import { PerformanceType } from '../../queries/getPerformanceTrend/getPerformanceTrendQuery.type';
import { getPerformanceTrendQueryOptions } from '../../queries/getPerformanceTrend/getPerformanceTrendQueryOptions';
import { TabItem } from '../../types/CustomTab.type';
import { minutesToHours } from '../../utils/minutesToHours';
import CustomTabs from '../common/CustomTab';
import { PerformanceItem, PerformanceTrends } from './PerformanceSection.type';
import StatisticPerformanceTabSummary from './StatisticPerformanceTabSummary';

const createPerformanceItems = (
  { acceptance, solve, returns, cancellations }: PerformanceTrends,
  summary?: GetPerformanceSummaryQueryResponseType,
  activePerformanceType?: PerformanceType,
): PerformanceItem[] => [
  {
    id: 'acceptance',
    label: 'زمان پذیرش',
    title: 'زمان پذیرش',
    unitText: '(دقیقه)',
    value:
      activePerformanceType === 'acceptance'
        ? (summary?.average_all_months ?? 0)
        : 0,
    countryAvgValue:
      activePerformanceType === 'acceptance'
        ? (summary?.national_average ?? 0)
        : 0,
    rate: activePerformanceType === 'acceptance' ? (summary?.score ?? 0) : 0,
    average:
      activePerformanceType === 'acceptance'
        ? (summary?.national_average ?? 0)
        : 0,
    labels: acceptance.map((item) => item.month_name),
    values: acceptance.map((item) => item.hours ?? 0),
    theme: 'green',
    modalTitle: PERFORMANCE_MODAL_CONTENT.acceptance.title,
    modalDescription: PERFORMANCE_MODAL_CONTENT.acceptance.description,
  },
  {
    id: 'solve',
    label: 'زمان رسیدگی',
    title: 'زمان رسیدگی',
    unitText: '(ساعت)',
    value:
      activePerformanceType === 'solve'
        ? minutesToHours(summary?.average_all_months ?? 0)
        : 0,
    countryAvgValue:
      activePerformanceType === 'solve'
        ? minutesToHours(summary?.national_average ?? 0)
        : 0,
    rate: activePerformanceType === 'solve' ? (summary?.score ?? 0) : 0,
    average:
      activePerformanceType === 'solve'
        ? minutesToHours(summary?.national_average ?? 0)
        : 0,
    labels: solve.map((item) => item.month_name),
    values: solve.map((item) => minutesToHours(item.hours ?? 0)),
    theme: 'blue',
    modalTitle: PERFORMANCE_MODAL_CONTENT.solve.title,
    modalDescription: PERFORMANCE_MODAL_CONTENT.solve.description,
  },
  {
    id: 'returns',
    label: 'برگشتی',
    title: 'برگشتی',
    unitText: '(پرونده)',
    value:
      activePerformanceType === 'returns' ? (summary?.total_files ?? 0) : 0,
    countryAvgValue:
      activePerformanceType === 'returns'
        ? (summary?.national_average ?? 0)
        : 0,
    rate: activePerformanceType === 'returns' ? (summary?.score ?? 0) : 0,
    average:
      activePerformanceType === 'returns'
        ? (summary?.national_average ?? 0)
        : 0,
    labels: returns.map((item) => item.month_name),
    values: returns.map((item) => item.count),
    maxValues: returns.map((item) => item.file_count ?? 0),
    theme: 'orange',
    integerValues: true,
    modalTitle: PERFORMANCE_MODAL_CONTENT.returns.title,
    modalDescription: PERFORMANCE_MODAL_CONTENT.returns.description,
  },
  {
    id: 'cancellations',
    label: 'انصرافی',
    title: 'انصرافی',
    unitText: '(پرونده)',
    value:
      activePerformanceType === 'cancellations'
        ? (summary?.total_files ?? 0)
        : 0,
    countryAvgValue:
      activePerformanceType === 'cancellations'
        ? (summary?.national_average ?? 0)
        : 0,
    rate: activePerformanceType === 'cancellations' ? (summary?.score ?? 0) : 0,
    average:
      activePerformanceType === 'cancellations'
        ? (summary?.national_average ?? 0)
        : 0,
    labels: cancellations.map((item) => item.month_name),
    values: cancellations.map((item) => item.count),
    maxValues: cancellations.map((item) => item.file_count ?? 0),
    theme: 'red',
    integerValues: true,
    modalTitle: PERFORMANCE_MODAL_CONTENT.cancellations.title,
    modalDescription: PERFORMANCE_MODAL_CONTENT.cancellations.description,
  },
];

export default function StatisticPerformanceSection() {
  const [activePerformanceType, setActivePerformanceType] =
    useState<PerformanceType>('acceptance');

  const { data: performanceTrend = [], isPending: isPerformanceTrendPending } =
    useQuery(
      getPerformanceTrendQueryOptions({
        performance_type: activePerformanceType,
      }),
    );
  const { data: performanceSummary, isPending: isPerformanceSummaryPending } =
    useQuery(
      getPerformanceSummaryQueryOptions({
        performance_type: activePerformanceType,
      }),
    );

  const isPerformanceLoading =
    isPerformanceTrendPending || isPerformanceSummaryPending;

  const performanceTrends: PerformanceTrends = {
    acceptance: [],
    solve: [],
    returns: [],
    cancellations: [],
    [activePerformanceType]: performanceTrend,
  };

  const performanceItems = createPerformanceItems(
    performanceTrends,
    performanceSummary,
    activePerformanceType,
  );

  const tabs: TabItem[] = performanceItems.map(
    ({
      id,
      label,
      title,
      unitText,
      value,
      countryAvgValue,
      rate,
      labels,
      values,
      average,
      maxValues,
      theme,
      modalTitle,
      modalDescription,
      integerValues,
    }) => ({
      id,
      label,
      content: isPerformanceLoading ? (
        <LoadingPage height="30vh" />
      ) : (
        <StatisticPerformanceTabSummary
          title={title}
          unitText={unitText}
          value={value}
          countryAvgValue={countryAvgValue}
          rate={rate}
          labels={labels}
          values={values}
          average={average}
          maxValues={maxValues}
          theme={theme}
          modalTitle={modalTitle}
          modalDescription={modalDescription}
          integerValues={integerValues}
        />
      ),
    }),
  );

  return (
    <Flex flexDirection="column" gap="5px">
      <Flex alignItems="center" gap="8px" margin="10px 0px">
        <ChartImage />
        <Typography textStyle="h1" color="#303030">
          شاخص‌ های عملکردی
        </Typography>
      </Flex>

      <Box>
        <CustomTabs
          items={tabs}
          variant="full"
          onChange={(tabId) => {
            setActivePerformanceType(tabId as PerformanceType);
          }}
        />
      </Box>
    </Flex>
  );
}
